import cx from 'classnames';
import copy from 'copy-to-clipboard';

import styles from './PaymentDetails.module.css';

import { ReactComponent as CopySvg } from '@frontend/icons/copy.svg';
import {
  BoxStyleProps,
  Button,
  Icon,
  StripedBox,
  Text,
  Tooltip,
} from '@frontend/ui-library';

interface PaymentDetailsProps extends BoxStyleProps {
  className?: string;
  isOffRamp: boolean;
  cryptoCurrency?: string;
  accumulatedCryptoAmount?: string;
  remainingCryptoAmount?: string;
  valueToCopy?: string;
  copyDisabled?: boolean;
  onCopy?: () => void;
}

export default function PaymentDetails({
  className,
  isOffRamp,
  cryptoCurrency,
  accumulatedCryptoAmount,
  remainingCryptoAmount,
  valueToCopy,
  copyDisabled,
  onCopy,
  ...otherProps
}: PaymentDetailsProps): JSX.Element {
  const handleCopy = () => {
    copy(valueToCopy || '');
    onCopy?.();
  };

  const hasAccumulatedAmount = accumulatedCryptoAmount !== '0';

  const cryptoAmountText = `${remainingCryptoAmount} ${cryptoCurrency}`;

  const sentCryptoAmountText = hasAccumulatedAmount
    ? `${accumulatedCryptoAmount} ${cryptoCurrency}`
    : null;

  return (
    <StripedBox className={cx(styles['base'], className)} {...otherProps}>
      <div className={styles['main-content']}>
        <Text
          as="div"
          size="tiny-callout"
          color="neutral-500"
          data-test-id="title"
        >
          {hasAccumulatedAmount
            ? `remaining amount to ${isOffRamp ? 'off ramp' : 'pay'}`
            : `amount to ${isOffRamp ? 'off ramp' : 'pay'}`}
        </Text>

        <div className={styles['amount-wrapper']}>
          <Text
            weight="700"
            size="big"
            color="royal-500"
            data-test-id="remaining-crypto-amount"
          >
            {cryptoAmountText}
          </Text>
        </div>

        {sentCryptoAmountText && (
          <div className={styles['already-sent-wrapper']}>
            <Text
              size="tiny-callout"
              color="alien-500"
              weight="700"
              data-test-id="sent-amount"
            >
              Already {isOffRamp ? 'Transferred' : 'Paid'}:{' '}
              {sentCryptoAmountText}
            </Text>
          </div>
        )}
      </div>
      {!copyDisabled && (
        <div className={styles['copy-button-wrapper']}>
          <Tooltip content="Copy amount">
            <Button
              className={styles['copy-button']}
              data-test-id="copy-amount-button"
              aria-label="copy amount"
              contentClassName={styles['copy-button-content']}
              size="large"
              variant="secondary"
              onClick={handleCopy}
            >
              <Icon size={20} component={<CopySvg />} />
            </Button>
          </Tooltip>
        </div>
      )}
    </StripedBox>
  );
}
